import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, Package, ShoppingCart } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function ConsumablesPage() {
    const { siteId } = useParams();
    const { user } = useAuth();
    const [site, setSite] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const isAdmin = user?.role === "HO_ADMIN";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, c] = await Promise.all([api.get(`/sites/${siteId}`), api.get(`/sites/${siteId}/consumables`)]);
            setSite(s.data); setRows(c.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId]);
    useEffect(() => { load(); }, [load]);

    async function update(itemId, patch) {
        try {
            await api.patch(`/sites/${siteId}/consumables/${itemId}`, patch);
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function logUsage(itemId, qty) {
        if (!qty || qty <= 0) return;
        try {
            const { data } = await api.post(`/sites/${siteId}/consumables/${itemId}/usage`, { qty: Number(qty) });
            if (data.cap_warning) toast.warning(data.cap_warning);
            else toast.success("Usage logged");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    const stockoutRows = rows.filter((r) => r.is_stockout);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/" className="text-xs text-gray-500 hover:text-[#1E3355] inline-flex items-center gap-1 mb-3"><ChevronLeft className="w-3 h-3" /> All sites</Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Consumables · Stock Register</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">{site?.name || "…"}</h1>
                </div>
                <div className="flex items-center gap-2">
                    <Link to={`/sites/${siteId}/reconciliation`} className="text-xs font-medium px-3 py-2 rounded-md bg-white border border-gray-200 text-gray-700 hover:text-[#1E3355]" data-testid="link-reconciliation">Monthly reconciliation</Link>
                    <Link to={`/sites/${siteId}/purchase`} className="text-xs font-medium px-3 py-2 rounded-md bg-white border border-gray-200 text-gray-700 hover:text-[#1E3355] inline-flex items-center gap-1" data-testid="link-purchase"><ShoppingCart className="w-3.5 h-3.5" /> Purchase queue</Link>
                </div>
            </div>

            {(site?.contract_notes || []).filter((n) => n.category === "consumables").map((n) => (
                <div key={n.id} className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid={`contract-banner-${n.id}`}>
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Contract clause</div>
                        <div className="text-sm text-amber-900 mt-0.5 font-medium">{n.text}</div>
                    </div>
                </div>
            ))}

            {stockoutRows.length > 0 && (
                <div className="mt-4 rounded-xl bg-red-50 border-2 border-[#E02B2B] p-4 flex items-start gap-3" data-testid="stockout-banner">
                    <AlertTriangle className="w-5 h-5 text-[#E02B2B] flex-shrink-0" />
                    <div>
                        <div className="font-display font-bold text-[#E02B2B]">STOCKOUT — Service deficiency</div>
                        <div className="text-sm text-gray-700 mt-0.5">
                            The following contracted items are at zero stock: <span className="font-semibold">{stockoutRows.map((r) => r.item.name).join(", ")}</span>. PM & HO have been notified.
                        </div>
                    </div>
                </div>
            )}

            {loading ? <div className="mt-6 h-40 rounded-xl bg-white animate-pulse" /> : (
                <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="consumables-table">
                            <thead className="bg-[#F4F6F9]">
                                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                    <th className="px-3 py-2">Item</th>
                                    <th className="px-3 py-2">Cat</th>
                                    <th className="px-3 py-2 text-center">Enabled</th>
                                    <th className="px-3 py-2 text-right">Stock</th>
                                    <th className="px-3 py-2 text-right">Threshold</th>
                                    <th className="px-3 py-2">Billing</th>
                                    <th className="px-3 py-2 text-center">Cap</th>
                                    <th className="px-3 py-2 text-center">Log usage</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {rows.map((r) => (
                                    <ConsumableRow key={r.item.id} row={r} update={update} logUsage={logUsage} isAdmin={isAdmin} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function ConsumableRow({ row, update, logUsage, isAdmin }) {
    const [qty, setQty] = useState("");
    const capPct = row.cap_qty && row.cap_used_cycle != null ? (row.cap_used_cycle / row.cap_qty) * 100 : null;
    return (
        <tr className={row.is_stockout ? "bg-red-50" : ""} data-testid={`consumable-row-${row.item.name}`}>
            <td className="px-3 py-2 font-medium text-[#1E3355] inline-flex items-center gap-1"><Package className="w-3.5 h-3.5 text-gray-400" /> {row.item.name} <span className="text-[10px] text-gray-400">/ {row.item.unit}</span>
                {row.has_open_pr && <span className="ml-2 text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">PR open</span>}
            </td>
            <td className="px-3 py-2 text-[10px] uppercase tracking-wider text-gray-500">{row.item.category}</td>
            <td className="px-3 py-2 text-center">
                <input type="checkbox" checked={!!row.enabled} onChange={(e) => update(row.item.id, { enabled: e.target.checked })} data-testid={`toggle-enabled-${row.item.name}`} />
            </td>
            <td className="px-3 py-2 text-right">
                <input type="number" defaultValue={row.current_stock} onBlur={(e) => Number(e.target.value) !== row.current_stock && update(row.item.id, { current_stock: Number(e.target.value) })} className="w-20 text-right rounded border border-gray-200 px-2 py-1 text-sm" data-testid={`stock-${row.item.name}`} />
            </td>
            <td className="px-3 py-2 text-right">
                <input type="number" defaultValue={row.min_threshold} onBlur={(e) => Number(e.target.value) !== row.min_threshold && update(row.item.id, { min_threshold: Number(e.target.value) })} className="w-20 text-right rounded border border-gray-200 px-2 py-1 text-sm" data-testid={`threshold-${row.item.name}`} />
            </td>
            <td className="px-3 py-2">
                <select defaultValue={row.billing_treatment} onChange={(e) => update(row.item.id, { billing_treatment: e.target.value })} className="text-xs rounded border border-gray-200 px-2 py-1" data-testid={`billing-${row.item.name}`}>
                    <option value="INCLUDED">Included</option>
                    <option value="BILLED_TO_CLIENT">Billed to client</option>
                </select>
            </td>
            <td className="px-3 py-2 text-center text-xs">
                {row.cap_qty ? (
                    <div className="inline-block min-w-[110px]">
                        <div className="tabular-nums font-medium">{row.cap_used_cycle?.toFixed(0) || 0}/{row.cap_qty}</div>
                        <div className="mt-0.5 w-full bg-gray-200 rounded-full h-1 overflow-hidden">
                            <div className={`h-1 rounded-full ${capPct >= 100 ? "bg-[#E02B2B]" : capPct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, capPct || 0)}%` }} />
                        </div>
                    </div>
                ) : isAdmin ? (
                    <input type="number" placeholder="Set" onBlur={(e) => e.target.value && update(row.item.id, { cap_qty: Number(e.target.value), cap_period: "monthly" })} className="w-16 text-right rounded border border-gray-200 px-1 py-0.5 text-xs" />
                ) : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-3 py-2 text-center">
                <div className="inline-flex items-center gap-1">
                    <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty" className="w-16 text-right rounded border border-gray-200 px-2 py-1 text-xs" data-testid={`usage-qty-${row.item.name}`} />
                    <button onClick={() => { logUsage(row.item.id, qty); setQty(""); }} className="text-xs font-medium text-[#1E3355] hover:bg-gray-100 rounded px-2 py-1" data-testid={`log-usage-${row.item.name}`}>Log</button>
                </div>
            </td>
        </tr>
    );
}
